import { ComponentPropsWithoutRef } from 'react';
import { VariantProps, cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export type SpinnerProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof spinnerVariants> & {
    color?:
      | 'achromatic'
      | 'achromatic-primary'
      | 'achromatic-info'
      | 'primary'
      | 'info'
      | 'warning'
      | 'success'
      | 'error';
    size?: '4xs' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg';
  };

function Spinner({ color = 'primary', size = 'lg', className, ...props }: SpinnerProps) {
  return <div className={cn(spinnerVariants({ color, size }), className)} {...props} />;
}

const spinnerVariants = cva(
  [
    'border-2',
    'border-l-white',
    'border-r-white',
    'border-b-white',
    'border-t-achromatic-disabled-dark',
    'rounded-full',
    'animate-spin',
  ],
  {
    variants: {
      color: {
        achromatic: [
          'border-l-achromatic-light',
          'border-r-achromatic-light',
          'border-b-achromatic-light',
        ],
        'achromatic-primary': [
          'border-l-achromatic-light',
          'border-r-achromatic-light',
          'border-b-achromatic-light',
        ],
        'achromatic-info': [
          'border-l-achromatic-light',
          'border-r-achromatic-light',
          'border-b-achromatic-light',
        ],
        primary: ['border-l-primary', 'border-r-primary', 'border-b-primary'],
        info: ['border-l-info', 'border-r-info', 'border-b-info'],
        warning: ['border-l-warning', 'border-r-warning', 'border-b-warning'],
        success: ['border-l-success', 'border-r-success', 'border-b-success'],
        error: ['border-l-error', 'border-r-error', 'border-b-error'],
      },
      size: {
        '4xs': ['w-1', 'h-1'],
        '3xs': ['w-2', 'h-2'],
        '2xs': ['w-4', 'h-4'],
        xs: ['w-[1.125rem]', 'h-[1.125rem]'],
        sm: ['w-6', 'h-6'],
        md: ['w-[1.875rem]', 'h-[1.875rem]'],
        lg: ['w-10', 'h-10'],
      },
    },
  }
);

export default Spinner;
